import {useCallback, useState} from 'react';
import {loadDocument as apiLoadDocument} from 'Common/api';
import type {AstView, Version} from 'rendition-core';

export function useLoadDocument(
    setView?: (view: AstView) => void,
    setSandboxLink?: (link: string) => void,
    setWebflowJson?: (json: string) => void,
    setPreviewImage?: (previewImage: string) => void
) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const loadDocument = useCallback(
        async (id: string, callback?: (doc: Version) => void) => {
            setLoading(true);
            const result = await apiLoadDocument(id);

            if (result.data.doc.state === 'error') {
                setLoading(false);
                setError(true);
                return;
            }

            if (!result.data.latestVersion) {
                setTimeout(() => loadDocument(id, callback), 1000); // retry in 1 second (up to 2 seconds to cut server costs in half).
                return;
            } else {
                const doc = result.data.doc;
                if (result.data.latestVersion.state === 'error') {
                    setLoading(false);
                    setError(true);
                    return;
                }
                if (setPreviewImage && doc.inputOptions?.figmaInputImageUrl) {
                    setPreviewImage(
                        doc.inputOptions.figmaInputImageUrl[doc.inputOptions.figmaInputImageUrl.length - 1]
                    );
                }
                if (setView) {
                    setView(JSON.parse(result.data.latestVersion.view));
                }

                if (setSandboxLink && result.data.latestVersion.sandboxUrl) {
                    setSandboxLink(result.data.latestVersion.sandboxUrl);
                }
                if (setWebflowJson && result.data.latestVersion.webflowJson) {
                    setWebflowJson(result.data.latestVersion.webflowJson);
                }
            }
            setLoading(false);
            callback?.(result.data.latestVersion);
            return result.data.latestVersion;
        },
        [setPreviewImage, setSandboxLink, setView, setWebflowJson]
    );

    return {loadDocument, loading, error, setError, setLoading};
}
